import React, { useState, useEffect, useRef } from 'react';
import PreAssessmentQuiz from '../components/PreAssessmentQuiz.js';
import { coursePackagesData, pathwaysData } from '../data/courseData';
import CoursePackageTile from '../components/CoursePackageTile';
import CourseDetailsSection from '../components/CourseDetailsSection';
import PathwaysSection from '../components/PathwaysSection.js';
import './LandingPage.css';

function LandingPage() {
    const [selectedCourse, setSelectedCourse] = useState(null);
    const detailsSectionRef = useRef(null);
    const [showQuiz, setShowQuiz] = useState(false);
    const [recommendedCourseIdByQuiz, setRecommendedCourseIdByQuiz] = useState(null);

    const handleCourseSelect = (course) => {
        if (selectedCourse && selectedCourse.id === course.id) {
            setSelectedCourse(null);
        } else {
            setSelectedCourse(course);
        }
    };

    const handleCloseDetails = () => {
        setSelectedCourse(null);
    };

    const handleQuizComplete = (recommendedId) => {
        setRecommendedCourseIdByQuiz(recommendedId);
    };

    const handleShowRecommendation = (courseId) => {
        const element = document.getElementById(`course-tile-${courseId}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setSelectedCourse(coursePackagesData.find(c => c.id === courseId) || null);
        }
    };

    const toggleQuizAndScroll = () => {
        const currentlyShowing = showQuiz;
        setShowQuiz(prev => !prev);
        setRecommendedCourseIdByQuiz(null);

        if (!currentlyShowing) {
            setTimeout(() => {
                document.getElementById('quiz-section-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 0);
        }
    };

    useEffect(() => {
        if (selectedCourse && detailsSectionRef.current) {
            detailsSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [selectedCourse]);

    return (
        <div className="page-wrapper">
            {/* ============================================= */}
            {/* Hero Section */}
            {/* ============================================= */}
            <header className="hero-section">
                <div className="hero-content-wrapper">
                    <h1>Transforming Adult Education</h1>
                    <p className="hero-tagline">Empowering your educators with proven strategies to engage, retain, and inspire adult learners.</p>

                    <div className="hero-video-container">
                        <video
                            className="hero-video"
                            src="https://adultliteracyandnumeracy-public-media.s3.ap-southeast-2.amazonaws.com/videos/welcome_to_the_website.mp4"
                            controls
                            playsInline
                        >
                            Your browser does not support the video tag.
                        </video>
                    </div>

                    <div className="hero-intro-blocks-container">
                        <div className="intro-block">
                            <h2>The Challenge</h2>
                            <p>Many adult learners arrive underprepared for vocational training, leading to high dropout rates and lost confidence. When educators aren't equipped with the right tools, potential is lost and both learners and organizations suffer.</p>
                        </div>
                        <div className="intro-block">
                            <h2>Our Solution</h2>
                            <p>We provide targeted professional development that equips your staff with the skills to motivate, teach, and unlock the natural learning ability in every adult. Our purpose is to help you build a thriving educational environment.</p>
                        </div>
                        <div className="intro-block">
                            <h2>Our Commitment</h2>
                            <ul className="offer-list">
                                <li>Drive measurable results with our proven professional development services</li>
                                <li>Customize training packages to your organization’s precise needs</li>
                                <li>Leverage 25 years of research-based, high-impact strategies</li>
                                <li>Partner with an experienced leader in adult education</li>
                            </ul>
                        </div>
                    </div>

                    <div className="hero-buttons-container">
                        <button className="hero-cta-button" onClick={() => document.getElementById('course-packages-section').scrollIntoView({ behavior: 'smooth' })}>Explore Course Packages</button>
                        <button className="hero-quiz-button" onClick={toggleQuizAndScroll}>
                            {showQuiz ? 'Close Course Finder' : 'Find The Right Course'}
                        </button>
                    </div>
                </div>
            </header>

            {/* ============================================= */}
            {/* Quiz Section */}
            {/* ============================================= */}
            <div id="quiz-section-anchor" style={{ scrollMarginTop: "80px" }}></div>
            {showQuiz && (
                <section className="quiz-section-wrapper">
                    <h2>Course Finder Quiz</h2>
                    <p>Answer a few quick questions to get a personalized course recommendation.</p>
                    <PreAssessmentQuiz
                        coursePackages={coursePackagesData}
                        onQuizComplete={handleQuizComplete}
                        onShowRecommendation={handleShowRecommendation}
                    />
                </section>
            )}

            {/* ============================================= */}
            {/* Pathways Section */}
            {/* ============================================= */}
            <PathwaysSection/>

            {/* ============================================= */}
            {/* Course Packages Section */}
            {/* ============================================= */}
            <section id="course-packages-section" className="course-packages-section">
                <h2>Our Featured Course Packages</h2>
                <p className="section-intro">Prefer to focus on a specific area? Choose any of our course packages individually to meet your immediate needs.</p>
                <div className="course-tiles-grid">
                    {coursePackagesData.map(course => (
                        <CoursePackageTile
                            key={course.id}
                            id={`course-tile-${course.id}`}
                            course={course}
                            onSelect={handleCourseSelect}
                            isSelected={selectedCourse && selectedCourse.id === course.id}
                            isRecommendedByQuiz={recommendedCourseIdByQuiz === course.id}
                        />
                    ))}
                </div>
            </section>

            {/* ============================================= */}
            {/* Course Details Section (Conditional) */}
            {/* ============================================= */}
            {selectedCourse && (
                <CourseDetailsSection
                    course={selectedCourse}
                    onClose={handleCloseDetails}
                    detailsSectionRef={detailsSectionRef}
                />
            )}
        </div>
    );
}

export default LandingPage;